import type { BrowserTask } from "@ondit/publishing";
import { ChipConfirmModal } from "@ondit/ui";
import { type ReactNode, useState } from "react";
import { useAccess } from "~/core/access";
import { ApiError, api } from "~/core/api";
import { useI18n, useT } from "~/core/i18n";

export type PublishResult = { state: string; published_url: string | null };

/**
 * The one confirmation anything leaves through. The composer arrives with a
 * payload it may still have to save, the calendar with a task that is already
 * complete — `resolveTask` is the only difference, and what the person is asked
 * stays the same either way.
 */
export function PublishConfirm({
	open,
	onOpenChange,
	entityId,
	platform,
	credentialId,
	detail,
	resolveTask,
	onPublished,
}: {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	entityId: string;
	platform: { name: string };
	credentialId: string;
	/** What exactly goes out, shown under the question. */
	detail?: ReactNode;
	/** The task to send; null aborts the publish without an error. */
	resolveTask: () => Promise<BrowserTask | null>;
	onPublished: (task: BrowserTask, result: PublishResult) => void;
}) {
	const t = useT();
	const { locale } = useI18n();
	const { editor } = useAccess();
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState("");
	return (
		<ChipConfirmModal
			open={open}
			onOpenChange={(next) => {
				if (!next) setError("");
				onOpenChange(next);
			}}
			title={t("publishing.platform.publish", { platform: platform.name })}
			text={t("publishing.platform.publishConfirm", { platform: platform.name })}
			dismissLabel={t("common.cancel")}
			confirm={{
				label: t("publishing.platform.confirm"),
				variant: "primary",
				pending: busy,
				pendingLabel: t("publishFlow.working"),
				disabled: !editor,
				onClick: () => {
					void (async () => {
						setBusy(true);
						setError("");
						try {
							const task = await resolveTask();
							if (!task) return;
							onPublished(task, await api.publishPlatformTask(entityId, task.id, credentialId));
							onOpenChange(false);
						} catch (e) {
							setError(e instanceof ApiError ? e.localized(locale) : String(e));
						} finally {
							setBusy(false);
						}
					})();
				},
			}}
		>
			{detail}
			{error && <p role="alert">{error}</p>}
		</ChipConfirmModal>
	);
}
